import React from "react";
import { Store } from "lucide-react";
import defaultAvatar from "../assets/profile.png";

// Sellers appear as their store, except to friends (accepted in both directions)
export const showsAsStore = (user) =>
  !!user?.seller && !(user.myStatus === "accepted" && user.friendStatus === "accepted");

export const getDisplayAvatar = (user) =>
  (showsAsStore(user) && user.seller.profile_image?.url) || user?.profile_picture_url;

export const getDisplayName = (user) =>
  (showsAsStore(user) && (user.seller.name?.trim() || user.seller.store_name?.trim())) ||
  (user?.account_type === "business" ? user?.business_name?.trim() : "") ||
  [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
  user?.username ||
  "User";

const inferMediaType = (url = "") => {
  if (/\.(jpe?g|png|gif|webp)(\?|$)/i.test(url)) return "image";
  if (/\.(mp3|m4a|ogg|wav)(\?|$)/i.test(url)) return "audio";
  if (/\.(mp4|mov|webm)(\?|$)/i.test(url)) return "video";
  return "file";
};

// media_url may hold [{ url, media_type }] (web) or plain URL strings; a shared location also keeps
// its latitude, longitude and address
export const normalizeMedia = (media) => {
  const list = Array.isArray(media) ? media : media ? [media] : [];
  return list
    .map((item) => {
      if (typeof item === "string") return { url: item, type: inferMediaType(item) };
      const url = item?.url || item?.fileUrl || "";
      if (!url) return null;
      const type = item.media_type || item.type || inferMediaType(url);
      return type === "location"
        ? { url, type, latitude: item.latitude, longitude: item.longitude, address: item.address }
        : { url, type };
    })
    .filter(Boolean);
};

export const getMessagePreview = (text, media) => {
  if (text && text.trim()) return text;
  const first = normalizeMedia(media)[0];
  if (!first) return "";
  if (first.type === "image") return "📷 Photo";
  if (first.type === "video") return "🎥 Video";
  if (first.type === "audio") return "🎤 Voice message";
  if (first.type === "location") return "📍 Location";
  return "📎 File";
};

export const formatChatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const SellerBadge = () => (
  <span className="inline-flex items-center gap-0.5 shrink-0 px-1.5 py-0.5 rounded-full bg-[#39B54A] text-white text-[10px] font-medium leading-none">
    <Store className="w-3 h-3" aria-hidden="true" />
    Seller
  </span>
);

const ChatItem = ({ chat, isActive, isOnline, currentUserId, onClick }) => {
  const preview = getMessagePreview(chat.lastMessage, chat.lastMessageMedia);
  const fromMe = chat.lastSenderId && Number(chat.lastSenderId) === Number(currentUserId);
  const highlighted = isActive || chat.unread > 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative w-full flex items-center gap-3 p-2.5 mb-3 rounded-xl border text-left transition-colors ${
        highlighted ? "border-[#39B54A]" : "border-[#E5E5E5] hover:border-[#39B54A]/60"
      } ${isActive ? "bg-[#F4FBF5]" : "bg-white hover:bg-[#FAFAFA]"}`}
    >
      <div className="relative shrink-0">
        <img
          src={chat.avatar || defaultAvatar}
          alt={chat.name}
          className="w-[52px] h-[52px] rounded-lg border border-[#E5E5E5] object-cover"
        />
        <span
          className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${
            isOnline ? "bg-[#39B54A]" : "bg-[#E53935]"
          }`}
          title={isOnline ? "Online" : "Offline"}
        />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 min-w-0">
          <h4 className="font-semibold text-sm text-[#000000] truncate">{chat.name}</h4>
          {chat.sellerId && <SellerBadge />}
        </div>
        <p className={`text-xs truncate ${chat.unread ? "text-[#000000] font-semibold" : "text-[#797C7B]"}`}>
          {preview ? `${fromMe ? "You: " : ""}${preview}` : chat.isNew ? "Start a conversation" : ""}
        </p>
        <span className="text-[11px] text-[#39B54A] italic">{formatChatTime(chat.lastMessageAt)}</span>
      </div>
      {chat.unread > 0 && (
        <span className="absolute -top-2 -right-2 bg-[#39B54A] text-white text-[10px] min-w-[20px] h-5 px-1 rounded-full flex items-center justify-center font-bold border-2 border-white">
          {chat.unread > 99 ? "99+" : chat.unread}
        </span>
      )}
    </button>
  );
};

export default ChatItem;
